/**
 * Device Detection Utilities
 * Centralized service for detecting device types, capabilities, and characteristics
 */

/**
 * Comprehensive mobile device detection
 * Uses multiple strategies for accurate mobile detection
 */
export const isMobileDevice = () => {
  // Multiple mobile detection strategies
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Basic user agent check
  const basicMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  
  // Touch capability check
  const touchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Screen size check (mobile-typical dimensions)
  const smallScreen = window.innerWidth <= 768;
  
  // Combine strategies: basic mobile detection OR (touch + small screen)
  return basicMobile || (touchScreen && smallScreen);
};

/**
 * Detect if device is specifically a tablet
 */
export const isTabletDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const isTablet = /ipad|android(?!.*mobile)|tablet/i.test(userAgent);
  const isLargeTouch = 'ontouchstart' in window && window.innerWidth >= 768 && window.innerWidth <= 1024;
  
  return isTablet || isLargeTouch;
};

/**
 * Detect if device is desktop
 */
export const isDesktopDevice = () => {
  return !isMobileDevice() && !isTabletDevice();
};

/**
 * Detect specific operating systems
 */
export const getOperatingSystem = () => {
  const userAgent = navigator.userAgent;
  
  if (/windows/i.test(userAgent)) return 'Windows';
  if (/macintosh|mac os x/i.test(userAgent)) return 'macOS';
  if (/linux/i.test(userAgent)) return 'Linux';
  if (/android/i.test(userAgent)) return 'Android';
  if (/iphone|ipad|ipod/i.test(userAgent)) return 'iOS';
  
  return 'Unknown';
};

/**
 * Detect if device has touch capability
 */
export const hasTouchCapability = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * Get device characteristics for debugging/analytics
 */
export const getDeviceInfo = () => {
  return {
    isMobile: isMobileDevice(),
    isTablet: isTabletDevice(),
    isDesktop: isDesktopDevice(),
    hasTouch: hasTouchCapability(),
    os: getOperatingSystem(),
    userAgent: navigator.userAgent,
    screenSize: {
      width: window.screen.width,
      height: window.screen.height
    },
    viewportSize: {
      width: window.innerWidth,
      height: window.innerHeight
    }
  };
};

/**
 * Detect specific browsers
 */
export const getBrowser = () => {
  const userAgent = navigator.userAgent;
  
  if (/chrome/i.test(userAgent) && !/edge/i.test(userAgent)) return 'Chrome';
  if (/firefox/i.test(userAgent)) return 'Firefox';
  if (/safari/i.test(userAgent) && !/chrome/i.test(userAgent)) return 'Safari';
  if (/edge/i.test(userAgent)) return 'Edge';
  if (/opera/i.test(userAgent)) return 'Opera';
  
  return 'Unknown';
};

/**
 * Check if MetaMask extension is likely available (desktop browsers)
 */
export const isMetaMaskExtensionLikely = () => {
  const browser = getBrowser();
  const isDesktop = isDesktopDevice();
  
  // MetaMask extension is available on desktop Chrome, Firefox, Edge, Brave
  return isDesktop && ['Chrome', 'Firefox', 'Edge'].includes(browser);
};

/**
 * Check if MetaMask mobile app is likely the best option
 */
export const isMetaMaskMobileLikely = () => {
  return isMobileDevice() || isTabletDevice();
};
